import { Component, computed, inject, OnInit, signal, Signal, WritableSignal, effect } from '@angular/core';

import {
    WorkflowForm,
    WorkflowFormComponent,
    WorkflowFormKey,
    WorkflowTaskDataService,
    WorkflowUtilsModule,
    TaskHintDisplayModule,
    UnclaimedModule,
} from '@lib/workflow-integration/forms';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IInvoiceTask, IInvoiceTaskPreview, InvoiceTask, InvoiceTaskPreview } from '../types/InvoiceTask';
import { InvoiceDetailComponent } from '../../detail/invoice-detail.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
    DialogOptions,
    ExplanationDialogService,
    IExplanationDialogResult,
    MobileModule,
    MobileService,
} from '@lib/util';
import { MatDialog } from '@angular/material/dialog';
import {
    CreateOrUpdateStandaloneInvoiceComponent,
    InvoiceEditDialogData,
} from '../../create-standalone/create-or-update-standalone-invoice.component';
import {
    NotificationMessageLevel,
    NotificationService,
    IFileResponse,
    isPdf,
    PdfPreviewComponent,
    View,
    createDeeplinkCompletionContext,
    DeeplinkCompletionAction,
    DeeplinkCompletionContextService,
} from '@lib/core';
import { ApproveInvoiceCompletionData } from './ApproveInvoiceCompletionData';
import { InvoicePossibleActionType } from '../../detail/types/InvoicePossibleActionType';
import { InvoiceDataService, IInvoiceValidationResponse } from '../../invoice-data.service';
import { AccountingDataRequest } from '../types/AccountingDataRequest';
import { InvoiceValidationMessage } from '../types/InvoiceValidationMessage.enum';
import { I18nService } from '@lib/i18n';
import { DocumentPdfSideBySideComponent, DocumentPdfSideBySideStore } from '@lib/document';
import { MaterialGroupDataRequest } from '../types/MaterialGroupDataRequest';

@WorkflowForm(
    marker('Rechnung freigeben'),
    [WorkflowFormKey.INVOICE_BASIC_APPROVAL_FORM],
    (dto: IInvoiceTaskPreview, displayName: string) => new InvoiceTaskPreview(dto, displayName),
    marker('Rechnung freigeben'),
)
@Component({
    selector: 'lib-invoice-basic-approval-form',
    imports: [
        InvoiceDetailComponent,
        WorkflowUtilsModule,
        TaskHintDisplayModule,
        TranslateModule,
        UnclaimedModule,
        PdfPreviewComponent,
        MobileModule,
        DocumentPdfSideBySideComponent,
    ],
    templateUrl: './invoice-basic-approval-form.component.html',
    styleUrl: './invoice-basic-approval-form.component.scss',
    providers: [DocumentPdfSideBySideStore],
})
export class InvoiceBasicApprovalFormComponent extends WorkflowFormComponent implements OnInit {
    public readonly invoiceFiles: Signal<IFileResponse[]>;
    public readonly showPdfPreview: Signal<boolean>;
    public readonly hasPdfFileAttachments: Signal<boolean>;
    public readonly task: WritableSignal<InvoiceTask | undefined> = signal(undefined);

    public readonly isInvoiceValid: WritableSignal<boolean> = signal(false);
    public readonly isInvoiceAccountingValid: WritableSignal<boolean> = signal(true);
    public readonly accountingWarnings: WritableSignal<readonly string[]> = signal([]);
    public readonly validationErrors: WritableSignal<string[] | undefined> = signal(undefined);
    public readonly isValidating: WritableSignal<boolean> = signal(false);
    public readonly isMobile: Signal<boolean>;
    public readonly pdfSideBySide = inject(DocumentPdfSideBySideStore);

    private readonly _taskDataService = inject(WorkflowTaskDataService);
    private readonly _invoiceDataService = inject(InvoiceDataService);
    private readonly _notificationService = inject(NotificationService);
    private readonly _route = inject(ActivatedRoute);
    private readonly _explanationDialog = inject(ExplanationDialogService);
    private readonly _router = inject(Router);
    private readonly _dialog = inject(MatDialog);
    private readonly _i18n = inject(I18nService);
    private readonly _mobileService = inject(MobileService);
    private readonly _deeplinkCompletionContext = inject(DeeplinkCompletionContextService);

    private readonly _pdfPreviewActive: WritableSignal<boolean> = signal(true);

    protected readonly InvoicePossibleActionType = InvoicePossibleActionType;

    constructor() {
        super();

        this.invoiceFiles = computed(() => {
            const task = this.task();
            if (task) {
                return task.invoice.files;
            }

            return [];
        });

        this.hasPdfFileAttachments = computed(() => {
            const files = this.invoiceFiles();

            return files.some((file) => isPdf(file.fileType));
        });

        this.isMobile = computed(() => {
            const mobileState = this._mobileService.isMobile();

            return mobileState.mobile;
        });

        this.showPdfPreview = computed(() => {
            const isMobile = this.isMobile();
            const hasPdfAttachments = this.hasPdfFileAttachments();
            const previewActive = this._pdfPreviewActive();

            return (!isMobile && hasPdfAttachments && previewActive) || this.pdfSideBySide.isOpen();
        });

        effect(() => {
            const currentTask = this.task();
            if (currentTask?.invoice?.id) {
                this.validate(currentTask.invoice.id);
            }
        });
    }

    ngOnInit(): void {
        this.taskId = this.taskId ? this.taskId : this._route.snapshot.params['taskId'];
        this.getTask();
    }

    public getTask() {
        if (this.taskId) {
            this._taskDataService.getSingleTaskById<IInvoiceTask>(this.taskId).subscribe((task: IInvoiceTask) => {
                this.task.set(new InvoiceTask(task));
            });
        }
    }

    public togglePdfPreview(): void {
        const hide = this.showPdfPreview();
        this.pdfSideBySide.close();
        this._pdfPreviewActive.set(!hide);
    }

    private validate(invoiceId: string): void {
        this.isValidating.set(true);

        this._invoiceDataService.validateInvoice(invoiceId).subscribe((res: IInvoiceValidationResponse) => {
            this.isValidating.set(false);
            if (res.valid) {
                // Invoice is valid - enable button, no messages
                this.isInvoiceValid.set(true);
                this.validationErrors.set(undefined);
            } else {
                // Invoice is invalid - disable button, show errors
                this.isInvoiceValid.set(false);
                const errorMessages =
                    res.errors
                        ?.map((error) =>
                            typeof error === 'string'
                                ? this.translateValidationCode(error)
                                : this.translateValidationCode(
                                      (error as { code?: string; message?: string }).code || '',
                                  ),
                        )
                        .filter((msg) => !!msg) || [];
                this.validationErrors.set(errorMessages);
            }
        });
    }

    decide(isApproved: boolean): void {
        if (!this._isCompleted) {
            if (!isApproved) {
                this._explanationDialog.open().subscribe((result: IExplanationDialogResult) => {
                    if (!result.cancelled) {
                        this._isCompleted = true;
                        this.complete(isApproved, result.explanation);
                    }
                });
            } else {
                this._isCompleted = true;
                this.complete(isApproved);
            }
        }
    }

    private async complete(isApproved: boolean, explanation?: string): Promise<void> {
        const task = this.task();
        if (task) {
            this._taskDataService
                .completeTask(task.id, new ApproveInvoiceCompletionData(isApproved, explanation))
                .subscribe({
                    next: () => {
                        this._deeplinkCompletionContext.completeRun(
                            createDeeplinkCompletionContext(
                                isApproved
                                    ? DeeplinkCompletionAction.INVOICE_APPROVED
                                    : DeeplinkCompletionAction.INVOICE_REJECTED,
                                task.invoice.invoiceNumber,
                            ),
                        );
                        this.completed.emit();
                        if (this.routeToListAfterCompletion) {
                            this._router.navigate([View.CLIENT, 'task', 'list'], {
                                replaceUrl: true,
                            });
                        }
                    },
                    error: () => (this._isCompleted = false),
                });
        }
    }

    openInvoiceEditDialog() {
        const task = this.task();
        if (!task) {
            return;
        }
        const options: DialogOptions = DialogOptions.LARGE(new InvoiceEditDialogData(task.invoice.id, task.id));

        // Hide underlying PDF preview to avoid multiple viewer instances
        const prevPdfPreviewActive = this._pdfPreviewActive();
        this._pdfPreviewActive.set(false);

        const ref = this._dialog.open(CreateOrUpdateStandaloneInvoiceComponent, options);
        // Trigger validation while dialog is open on every accounting change
        ref.componentInstance.invoiceChanged.subscribe(() => {
            this.validate(task.invoice.id);
        });

        ref.afterClosed().subscribe((result: { updated: boolean } | undefined) => {
            // Restore previous PDF preview state
            this._pdfPreviewActive.set(prevPdfPreviewActive);

            // Optionally refresh task/invoice if updated
            if (result?.updated) {
                this.getTask();
                this.validate(task.invoice.id);
            }
        });
    }

    onChangeAccounting(data: AccountingDataRequest) {
        if (data.accountingChangeData.value !== undefined) {
            this._invoiceDataService.saveAccountingValue(data).subscribe({
                next: () => {
                    this.getTask();
                    const message = marker('Änderung übernommen');
                    this._notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                    const id = this.task()?.invoice.id;
                    if (id) {
                        this.validate(id);
                    }
                },
                error: () => {
                    const message = marker('Speichern fehlgeschlagen');
                    this._notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                    this.getTask();
                    const id = this.task()?.invoice.id;
                    if (id) {
                        this.validate(id);
                    }
                },
            });
        } else {
            this._invoiceDataService.deleteAccountingValue(data).subscribe({
                next: () => {
                    this.getTask();
                    const id = this.task()?.invoice.id;
                    if (id) {
                        this.validate(id);
                    }
                },
                error: () => {
                    const message = marker('Löschen fehlgeschlagen');
                    this._notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
        }
    }

    onAllAccountingChanged() {
        this.getTask();
        const message = marker('Änderung übernommen');
        this._notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
        const id = this.task()?.invoice.id;
        if (id) {
            this.validate(id);
        }
    }

    onAccountingValidationChanged(allValid: boolean): void {
        this.isInvoiceAccountingValid.set(allValid);
    }

    onAccountingWarningsChanged(warnings: readonly string[]): void {
        this.accountingWarnings.set(warnings);
    }

    onChangeMaterialGroup(data: MaterialGroupDataRequest): void {
        const request$ = data.materialGroupCode
            ? this._invoiceDataService.saveItemMaterialGroup(data.invoiceId, data.invoiceItemId, data.materialGroupCode)
            : this._invoiceDataService.deleteItemMaterialGroup(data.invoiceId, data.invoiceItemId);

        request$.subscribe({
            next: () => {
                this.getTask();
                const message = marker('Änderung übernommen');
                this._notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
                const id = this.task()?.invoice.id;
                if (id) {
                    this.validate(id);
                }
            },
            error: () => {
                const message = marker('Speichern fehlgeschlagen');
                this._notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                this.getTask();
                const id = this.task()?.invoice.id;
                if (id) {
                    this.validate(id);
                }
            },
        });
    }

    private translateValidationCode(code: string): string {
        switch (code) {
            case InvoiceValidationMessage.INVOICE_CURRENCY_NOT_MATCHING_CHARGE_CURRENCY:
                return this._i18n.translateInstant(
                    'Die Rechnungswährung muss mit der Währung der Positionen übereinstimmen',
                );
            case InvoiceValidationMessage.INVOICE_CURRENCY_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Währung muss angegeben werden, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_MUST_HAVE_ITEMS:
                return this._i18n.translateInstant(
                    'Die Rechnung muss mindestens eine Position enthalten, bevor sie erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_NOT_IN_STATE_UNAPPROVED:
                return this._i18n.translateInstant(
                    "Die Rechnung muss im Status  'freizugeben' sein, um freigegeben werden zu können",
                );
            case InvoiceValidationMessage.INVOICE_SUPPLIER_ADDRESS_MUST_HAVE_VAT:
                return this._i18n.translateInstant(
                    'Die Adresse des Lieferanten muss eine USt-IdNr. haben, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_SUPPLIER_ADDRESS_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Adresse des Lieferanten muss angegeben werden, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_SUPPLIER_NOT_SET:
                return this._i18n.translateInstant(
                    'Der Lieferant muss angegeben werden, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_DEPARTMENT_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Abteilung muss angegeben werden, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_ACCOUNTING_COST_CENTER_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Kostenstelle muss gesetzt sein, bevor die Rechnung freigegeben werden kann',
                );
            case InvoiceValidationMessage.INVOICE_PAYMENT_DATE_NOT_SET:
                return this._i18n.translateInstant(
                    'Das Fälligkeitsdatum muss angegeben werden, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_DATE_NOT_SET:
                return this._i18n.translateInstant(
                    'Das Rechnungsdatum muss angegeben werden, bevor die Rechnung erstellt wird',
                );
            case InvoiceValidationMessage.INVOICE_INVOICE_ADDRESS_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Rechnungsadresse muss angegeben werden, bevor die Rechnung erstellt wird',
                );

            case InvoiceValidationMessage.ALL_INVOICE_CHARGE_MUST_HAVE_NAME:
                return this._i18n.translateInstant('Alle zusätzlichen Kosten müssen einen Namen haben');

            case InvoiceValidationMessage.ALL_INVOICE_ITEMS_MUST_HAVE_NAME:
                return this._i18n.translateInstant('Alle Rechnungspositionen müssen einen Namen haben');
            case InvoiceValidationMessage.INVOICE_NUMBER_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Rechnungsnummer muss angegeben werden, bevor die Rechnung erstellt wird.',
                );
            case InvoiceValidationMessage.INVOICE_MISSING_MANDATORY_ATTACHMENT:
                return this._i18n.translateInstant('Rechnungsdatei erforderlich: Bitte laden Sie eine Datei hoch');
            case InvoiceValidationMessage.INVOICE_MATERIAL_GROUP_NOT_SET:
                return this._i18n.translateInstant(
                    'Die Warengruppe muss an allen Rechnungspositionen gesetzt sein, bevor die Rechnung freigegeben werden kann',
                );
            default:
                return code;
        }
    }
}
